import { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Loader2, TrendingUp, Package, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatBRL } from "@/lib/api";

export default function ReportPanel() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goal, setGoal] = useState(0);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/report?period=${period}`);
      setData(data.data);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => { setGoal(data.revenue_goal || 0); setGoalInput(String(data.revenue_goal || 0)); }).catch(() => {});
  }, []);

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      const { data } = await api.put("/admin/settings", { revenue_goal: parseFloat(String(goalInput).replace(",", ".")) || 0 });
      setGoal(data.revenue_goal || 0);
      setGoalInput(String(data.revenue_goal || 0));
      toast.success("Meta de receita atualizada");
    } catch {
      toast.error("Erro ao salvar meta");
    } finally {
      setSavingGoal(false);
    }
  };

  const totalOrders = data.reduce((a, b) => a + b.orders, 0);
  const totalRevenue = data.reduce((a, b) => a + b.revenue, 0);
  const currentRevenue = data.length ? data[data.length - 1].revenue : 0;
  const goalPct = goal > 0 ? Math.min(100, Math.round((currentRevenue / goal) * 100)) : 0;

  return (
    <div data-testid="report-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">Relatórios</h1>
        <div className="inline-flex rounded-full bg-slate-100 p-1">
          {[["week", "Semanal"], ["month", "Mensal"]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`report-period-${v}`}
              onClick={() => setPeriod(v)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${period === v ? "bg-white shadow text-[#0F294A]" : "text-slate-500"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#0F294A]/10 text-[#0F294A] mb-3"><Package className="h-5 w-5" /></span>
          <p className="font-heading font-extrabold text-2xl text-slate-900">{totalOrders}</p>
          <p className="text-xs text-slate-500">Pedidos no período</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 mb-3"><TrendingUp className="h-5 w-5" /></span>
          <p className="font-heading font-extrabold text-2xl text-slate-900">{formatBRL(totalRevenue)}</p>
          <p className="text-xs text-slate-500">Receita confirmada</p>
        </div>
      </div>

      {period === "month" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="revenue-goal">
          <span className="grid place-items-center h-11 w-11 rounded-xl bg-amber-100 text-amber-600 shrink-0"><Target className="h-5 w-5" /></span>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900">Meta mensal de receita</p>
            <p className="text-sm text-slate-500">
              {goal > 0
                ? `${goalPct}% da meta atingido neste mês (${formatBRL(currentRevenue)} de ${formatBRL(goal)})`
                : "Defina uma meta para acompanhar seu progresso no gráfico."}
            </p>
            {goal > 0 && (
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden max-w-md">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${goalPct}%` }} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-36">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">R$</span>
              <Input data-testid="goal-input" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} className="pl-9" />
            </div>
            <Button data-testid="goal-save" onClick={saveGoal} disabled={savingGoal} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1.5">
              {savingGoal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />} Salvar
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="report-orders-chart">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Pedidos por {period === "week" ? "semana" : "mês"}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                <Tooltip cursor={{ fill: "rgba(15,41,74,0.05)" }} />
                <Bar dataKey="orders" name="Pedidos" fill="#0F294A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="report-revenue-chart">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Receita por {period === "week" ? "semana" : "mês"}</h3>
            {totalRevenue === 0 ? (
              <div className="h-[280px] grid place-items-center text-center text-slate-400">
                <div>
                  <TrendingUp className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Nenhuma receita confirmada no período.</p>
                  <p className="text-xs">Os valores aparecem aqui após pagamentos aprovados.</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={70} tickFormatter={(v) => `R$${v}`} />
                  <Tooltip cursor={{ fill: "rgba(5,150,105,0.05)" }} formatter={(v) => [formatBRL(v), "Receita"]} />
                  <Bar dataKey="revenue" name="Receita" fill="#059669" radius={[6, 6, 0, 0]} />
                  {period === "month" && goal > 0 && (
                    <ReferenceLine y={goal} stroke="#f59e0b" strokeDasharray="5 4" strokeWidth={2} label={{ value: `Meta ${formatBRL(goal)}`, position: "insideTopRight", fill: "#b45309", fontSize: 11 }} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
